/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import {
  GET_INFINITE_MESSAGES_QUERY_KEY,
  DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS,
  TGetMessagesResponse,
} from '@/features/cases/case/api/get-messages.api';

/**
 *
 */
export const PatchUserLabelChatInputSchema = z.object({
  caseId: z.string().min(1),
  messageId: z.string().min(1),
  // labelChatIds: z.array(z.string().min(1)).min(1),
  labelChats: z.array(
    z.object({
      labelChatId: z.string().min(1),
      name: z.string().min(1),
      color: z.string().min(1),
    })
  ),
});

export type TPatchUserLabelChatInput = z.infer<typeof PatchUserLabelChatInputSchema>;
export type TPatchUserLabelChatResponse = ApiResponse<{
  message: string;
}>;

export const patchUserLabelChat = async (data: TPatchUserLabelChatInput): Promise<TPatchUserLabelChatResponse> => {
  const url = `/chat/update-label-chat/${data.messageId}`;

  const payload = {
    labelChatIds: data.labelChats.map((labelChat) => labelChat.labelChatId),
  };

  return axiosInstance.put(url, payload);
};

/**
 * Use Patch User Label Chat React-Query Hook
 */
type UsePatchUserLabelChatOptions = {
  mutationConfig?: MutationConfig<typeof patchUserLabelChat>;
};

export const usePatchUserLabelChat = ({ mutationConfig }: UsePatchUserLabelChatOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: patchUserLabelChat,
    ...restConfig,
  });
};

/**
 * Update User Label Chat Optimistically
 */

type UsePatchUserLabelChatOptimisticallyOptions = {
  mutationConfig?: MutationConfig<typeof patchUserLabelChat>;
};

type TPatchUserLabelChatOptimisticallyContext = {
  previousData: TGetMessagesResponse | undefined;
};

export const usePatchUserLabelChatOptimistically = ({ mutationConfig }: UsePatchUserLabelChatOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: patchUserLabelChat,
    onMutate: async (data: TPatchUserLabelChatInput) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [GET_INFINITE_MESSAGES_QUERY_KEY, data.caseId, DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS],
      });

      // Find the correct query key (you may need to pass caseId as part of data)
      const caseId = data.caseId; // Make sure your input includes this!
      const previousData = queryClient.getQueryData<TGetMessagesResponse>([
        GET_INFINITE_MESSAGES_QUERY_KEY,
        caseId,
        DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS,
      ]);

      // Optimistically update the cache
      queryClient.setQueryData(
        [GET_INFINITE_MESSAGES_QUERY_KEY, caseId, DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS],
        (old: any) => {
          if (!old?.pages) return old;

          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              payload: page.payload.map((msg: any) =>
                msg.messageId === data.messageId ? { ...msg, labelChats: data.labelChats } : msg
              ),
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _data, context) => {
      // Rollback on error
      if ((context as TPatchUserLabelChatOptimisticallyContext)?.previousData) {
        const caseId = _data.caseId;
        queryClient.setQueryData(
          [GET_INFINITE_MESSAGES_QUERY_KEY, caseId, DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS],
          (context as TPatchUserLabelChatOptimisticallyContext).previousData
        );
      }
    },
    ...restConfig,
  });
};
